import {
  ArrowBack,
  Cancel as IgnoredIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useContext, useEffect, useState } from 'react'

import { useProviderDetails } from '../../../providers/ProviderDetails/ProviderDetails.provider'
import {
  Assignment,
  MultipleChoiceOptions,
  Question,
  QuestionnaireContext,
  QuestionnaireContextInterface,
  ScaleOptions,
  Submission,
  SubmissionAnswer,
} from '../../../providers/Questionnaire/Questionnaire.provider'
import { themeColors } from '../../../theme'
import { dateFormatters } from '../../../utilities/HelperFunctions'
import { QuestionnaireGroupSummary } from './QuestionnaireList'

interface ConfirmState {
  open: boolean
  action: 'start' | 'stop'
  assignmentId?: number
}

interface QuestionnaireManagementProps {
  questionnaireGroupSummary: QuestionnaireGroupSummary
  patientId: number
  recurrenceWeeks?: number
  recurrenceIntervalDays?: number
  onBack: () => void
}

export const QuestionnaireManagement: React.FC<
  QuestionnaireManagementProps
> = ({
  questionnaireGroupSummary,
  patientId,
  recurrenceWeeks = 0,
  recurrenceIntervalDays = 0,
  onBack,
}) => {
  const {
    getPatientAssignments,
    getAssignmentSubmissions,
    questionnaireSubmissions,
    startAssignment,
    stopAssignment,
  } = useContext(QuestionnaireContext) as QuestionnaireContextInterface

  const { providerInformation } = useProviderDetails()

  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  // Scores modal state
  const [scoresOpen, setScoresOpen] = useState(false)
  const [selectedAssignment, setSelectedAssignment] =
    useState<Assignment | null>(null)
  const [subsLoading, setSubsLoading] = useState(false)

  // Confirm dialog state
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    action: 'start',
  })

  const fetchData = async () => {
    const data = await getPatientAssignments(patientId)
    setAssignments(data)
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [questionnaireGroupSummary.questionnaireId, patientId])

  const activeAssignment = assignments.find((a) => a.isActive)

  //   start
  const handleStart = useCallback(async () => {
    setActionLoading(true)
    await startAssignment({
      questionnaireId: questionnaireGroupSummary.questionnaireId,
      patientId: patientId,
      recurrenceWeeks: 8,
      recurrenceIntervalDays: 7, // weekly
    })

    const newAssignment: Assignment = {
      id: Date.now(),
      questionnaireTitle: questionnaireGroupSummary.title,
      questionnaireHasScoring: true,
      startDate: new Date().toISOString(),
      scheduledEndDate: new Date(
        Date.now() + recurrenceWeeks * recurrenceIntervalDays * 86400000
      ).toISOString(),
      terminatedOn: null,
      isActive: true,
      assignedByProviderName: providerInformation?.name || '',
      submissionCount: 0,
    }

    setAssignments((prev) => [newAssignment, ...prev])
    setActionLoading(false)
    setConfirm({ open: false, action: 'start' })
  }, [questionnaireGroupSummary.title, recurrenceWeeks, recurrenceIntervalDays])

  //  stop
  const handleStop = useCallback(async (assignmentId: number) => {
    setActionLoading(true)
    await stopAssignment(assignmentId)

    await fetchData()
    setActionLoading(false)
    setConfirm({ open: false, action: 'stop' })
  }, [])

  // get submissions
  const handleOpenQuestionnaireResult = useCallback(
    async (assignment: Assignment) => {
      setSelectedAssignment(assignment)
      setScoresOpen(true)
      setSubsLoading(true)

      await getAssignmentSubmissions(assignment.id)

      setSubsLoading(false)
    },
    []
  )

  const handleConfirmAction = useCallback(() => {
    if (confirm.action === 'start') handleStart()
    if (confirm.action === 'stop' && confirm.assignmentId)
      handleStop(confirm.assignmentId)
  }, [confirm, handleStart, handleStop])

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress />
      </Box>
    )
  }

  // 计算总分
  const calculateTotalScore = (questions: Question[]): number => {
    return questions.reduce((total: number, question: Question): number => {
      switch (question.questionType) {
        case 'boolean':
          return total + (question.points ?? 1)
        case 'scale': {
          const scaleOptions = question.options as ScaleOptions
          return total + (scaleOptions?.max ?? 5) // default scale top is 5
        }
        case 'multiple_choice': {
          const options = question.options as MultipleChoiceOptions
          if (options?.allowMultiple) {
            return (
              total +
              (options?.choices?.reduce((sum, c) => sum + (c.points ?? 0), 0) ??
                0)
            )
          }
          return (
            total +
            Math.max(...(options?.choices?.map((c) => c.points ?? 0) ?? [0]))
          )
        }
        default:
          return total
      }
    }, 0)
  }

  // evaluate how much points patients got from the answer they select
  const renderAnswerValuePoints = (answer: SubmissionAnswer) => {
    if (answer.questionType === 'boolean') {
      if (answer.answerValue === 'yes') {
        return answer.questionPoints ?? 1
      }
      return 0
    }
    // todo: add other types for future question types
  }

  return (
    <Box>
      <Stack>
        <Button
          variant='outlined'
          style={{ width: 130, marginBottom: 10, fontSize: 15 }}
          startIcon={<ArrowBack />}
          onClick={onBack}
        >
          Go back
        </Button>

        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ marginY: 2 }}
        >
          <Box>
            <Typography variant='h6' fontWeight={600} padding={0}>
              {questionnaireGroupSummary.title}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {recurrenceWeeks}-week program, once every{' '}
              {recurrenceIntervalDays} days
            </Typography>
          </Box>

          <Stack direction='row' spacing={1}>
            {!activeAssignment ? (
              <Button
                variant='contained'
                startIcon={<StartIcon />}
                onClick={() => setConfirm({ open: true, action: 'start' })}
                disabled={actionLoading}
                style={{ fontSize: 15 }}
              >
                Start Questionnaire
              </Button>
            ) : (
              <>
                <Button
                  variant='outlined'
                  color='error'
                  startIcon={<StopIcon />}
                  onClick={() =>
                    setConfirm({
                      open: true,
                      action: 'stop',
                      assignmentId: activeAssignment.id,
                    })
                  }
                  disabled={actionLoading}
                  style={{ fontSize: 15 }}
                >
                  Stop
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* Assignments Table */}
      {assignments.length === 0 ? (
        <Alert severity='info'>No assignments for this patient yet.</Alert>
      ) : (
        <TableContainer component={Paper} variant='outlined'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Scheduled End</TableCell>
                <TableCell>Stopped On</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align='center'>Responses</TableCell>
                <TableCell align='center'>Latest Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment: Assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    <StatusChip assignment={assignment} />
                  </TableCell>

                  <TableCell>{formatDate(assignment.startDate)}</TableCell>

                  <TableCell>
                    {formatDate(assignment.scheduledEndDate)}
                  </TableCell>

                  <TableCell>
                    {assignment.terminatedOn
                      ? formatDate(assignment.terminatedOn)
                      : '—'}
                  </TableCell>

                  <TableCell>{assignment.assignedByProviderName}</TableCell>

                  <TableCell align='center'>
                    <Chip
                      label={`${assignment.submissionCount} / ${recurrenceWeeks}`}
                      size='small'
                      variant='outlined'
                      onClick={() => handleOpenQuestionnaireResult(assignment)}
                    />
                  </TableCell>

                  <TableCell align='center'>
                    <Tooltip title='View Scores'>
                      <IconButton
                        color='primary'
                        size='small'
                        sx={{ padding: 1, borderRadius: 1 }}
                        onClick={() =>
                          handleOpenQuestionnaireResult(assignment)
                        }
                      >
                        {assignment.latestScore ?? '/'}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* view submission result/score Modal */}
      <Dialog
        open={scoresOpen}
        onClose={() => setScoresOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Box>
              <Typography variant='h6' fontWeight={600} padding={0}>
                Questionnaire Result
              </Typography>
              {selectedAssignment && (
                <Typography variant='body2' color='text.secondary'>
                  {formatDate(selectedAssignment.startDate)} —{' '}
                  {selectedAssignment.terminatedOn
                    ? formatDate(selectedAssignment.terminatedOn)
                    : formatDate(selectedAssignment.scheduledEndDate)}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setScoresOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {subsLoading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : questionnaireSubmissions.submissions.length === 0 ? (
            <Alert severity='info'>No submissions recorded yet.</Alert>
          ) : (
            <Stack spacing={2}>
              {questionnaireSubmissions.submissions.map(
                (submission: Submission, index) => (
                  <Card key={submission.id} variant='outlined'>
                    <CardContent>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        sx={{ mb: submission.isIgnored ? 0 : 1 }}
                      >
                        <Typography variant='subtitle2' fontWeight={600}>
                          Week{' '}
                          {questionnaireSubmissions.submissions.length - index}
                          {/* TODO!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! make sure it's questionnaire push-notification sent to patient time */}
                          {/* {dateFormatters.time(selectedAssignment?.startDate)} */}
                        </Typography>

                        {submission.isIgnored ? (
                          <Chip
                            icon={<IgnoredIcon />}
                            label='Ignored'
                            color='warning'
                            size='small'
                            sx={{
                              color: themeColors.red,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Chip
                              icon={<CheckIcon />}
                              label='Completed'
                              color='success'
                              size='small'
                              sx={{
                                color: '#16A34A',
                              }}
                            />
                            <Typography variant='body2'>
                              on {formatDate(submission.completedAt)} at{' '}
                              {dateFormatters.time(submission.completedAt)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      {!submission.isIgnored && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Table size='small'>
                            <TableBody>
                              {submission.answers.map(
                                (answer: SubmissionAnswer, i) => (
                                  <TableRow key={i}>
                                    <TableCell
                                      sx={{
                                        border: 'none',
                                        py: 0.5,
                                        pl: 0,
                                      }}
                                    >
                                      <Typography variant='body2'>
                                        {answer.sortOrder}.
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        border: 'none',
                                        padding: 0,
                                        py: 0.5,
                                        pl: 0,
                                      }}
                                    >
                                      <Typography variant='body2'>
                                        {answer.questionContent} (
                                        {answer.questionPoints ?? 1})
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      align='right'
                                      sx={{
                                        border: 'none',
                                        py: 0.5,
                                        pr: 0,
                                      }}
                                    >
                                      <BooleanAnswerChip
                                        value={answer.answerValue}
                                      />
                                    </TableCell>
                                    <TableCell
                                      align='right'
                                      sx={{
                                        border: 'none',
                                        py: 0.5,
                                        pr: 0,
                                      }}
                                    >
                                      <Typography>
                                        {renderAnswerValuePoints(answer)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>

                          {activeAssignment?.questionnaireHasScoring && (
                            <Box
                              sx={{
                                mt: 1.5,
                                pt: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                fontSize={18}
                              >
                                Score
                              </Typography>
                              <Typography
                                variant='body2'
                                fontWeight={700}
                                fontSize={18}
                                color={
                                  submission.score && submission.score >= 5
                                    ? themeColors.red
                                    : themeColors.green
                                }
                              >
                                {submission.score} /{' '}
                                {calculateTotalScore(
                                  questionnaireSubmissions.questions
                                )}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                )
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false, action: 'start' })}
      >
        <DialogTitle>
          {confirm.action === 'start' && 'Start Questionnaire?'}
          {confirm.action === 'stop' && 'Stop Questionnaire?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            {confirm.action === 'start' &&
              `This will start the ${questionnaireGroupSummary.title} for this patient. They will receive it once every ${recurrenceIntervalDays} days (weekly) for ${recurrenceWeeks} weeks.`}
            {confirm.action === 'stop' &&
              'This will stop the current questionnaire period. The patient will no longer receive this questionnaire. You can start it again if needed.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, action: 'start' })}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color={confirm.action === 'stop' ? 'error' : 'primary'}
            disabled={actionLoading}
            onClick={handleConfirmAction}
          >
            {actionLoading ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

//  Helpers

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

const BooleanAnswerChip: React.FC<{ value: string }> = ({ value }) => {
  const lower = value.toLowerCase()
  if (lower === 'yes')
    return (
      <Chip
        label='Yes'
        size='small'
        sx={{
          backgroundColor: '#FEF2F2',
          color: themeColors.red,
          border: '1px solid #FECACA',
        }}
      />
    )
  if (lower === 'no')
    return (
      <Chip
        label='No'
        size='small'
        sx={{
          backgroundColor: '#DCFCE7',
          color: themeColors.green,
          border: '1px solid #BBF7D0',
        }}
      />
    )
  return <Chip label={value} size='small' variant='outlined' />
}

const StatusChip: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
  if (assignment.isActive) {
    return <Chip label='Active' color='success' size='small' />
  }
  if (assignment.terminatedOn) {
    return <Chip label='Stopped' color='error' size='small' />
  }
  return <Chip label='Completed' size='small' />
}
